import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { notification } from 'antd';
import { FiArrowRight, FiCheck, FiChevronDown, FiX } from 'react-icons/fi';
import InfoTooltip from 'components/UI/InfoTooltip/InfoTooltip';
import HostPlanPurchaseService, { HostPlanPurchaseSummary } from 'service/HostPlanPurchaseService';
import { ensureZohoReady, openZohoPayment, ZohoPaymentsSession } from 'library/utils/zohoCheckout';
import { markAccountPremium, settlePurchaseStatus } from 'library/utils/hostPlan';
import { isAdminUser } from 'library/utils/isAdmin';
import { useOnboardingBackConfirm } from 'library/hooks/useOnboardingBackConfirm';
import {
  OnboardingShell,
  GhostButton,
  PrimaryButton,
  OutlineButton,
  IMPORT_SECTIONS,
  ob,
  bp,
} from 'components/Onboarding';
// @ts-ignore — JS module shared with the public pricing page
import { BHK_OPTIONS, DURATIONS, PRICE_TABLE, featureComparison } from 'containers/Pricing/Pricing.data';

const formatInr = (value: number): string =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);

type DurationKey = 'three' | 'six' | 'twelve';

const VERIFY_POLL_INTERVAL_MS = 2000;
const VERIFY_POLL_ATTEMPTS = 15;
/** A PENDING purchase older than this is stale, not a payment that is still confirming. */
const PENDING_PURCHASE_MAX_AGE_MS = 30 * 60 * 1000;

const HostingPlanScreen: React.FC = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(useLocation().search);
  const hostPropertyId = searchParams.get('id');
  /** Arrived from the "Let us list it for you" import: shows that flow's rail and steps. */
  const isImportFlow = searchParams.get('flow') === 'import';

  const { propertyId, offerings, offeringsMap } = useSelector((state: any) => ({
    propertyId: state.propertyId,
    offerings: state.offerings,
    offeringsMap: state.offeringsMap,
  }));

  const resolvePropertyId = (): string | null =>
    hostPropertyId ||
    (propertyId ? String(propertyId) : null) ||
    localStorage.getItem('newPropertId') ||
    localStorage.getItem('id');

  /* The listing's own bedroom count (0 when unknown). */
  const listingBedrooms = (() => {
    const key = Object.keys(offeringsMap || {}).find((k) => {
      const entry = offeringsMap[k];
      const label = typeof entry === 'string' ? entry : entry?.detailsName || entry?.name || '';
      return /bedroom/i.test(String(label));
    });
    return key ? Number(offerings?.[key] || 0) : 0;
  })();
  /* Start the BHK picker on it. */
  const listingBhk = Math.min(8, Math.max(1, listingBedrooms || 1));
  /* A plan only attaches to a listing of the same BHK size, so with a known listing the size is fixed. */
  const bhkLocked = listingBedrooms > 0 && !!resolvePropertyId();

  const [bhk, setBhk] = useState<number>(listingBhk);
  const bhkTouched = useRef(false);
  const [duration, setDuration] = useState<DurationKey>('twelve');
  const [paying, setPaying] = useState(false);
  // Which plan card is highlighted. Host Pro is pre-selected as the recommended plan.
  const [plan, setPlan] = useState<'starter' | 'pro'>('pro');
  // True once a paid Host Pro plan is already attached to this listing (e.g. the host comes back
  // here from the Review page), so Continue doesn't ask them to pay again.
  const [proPaid, setProPaid] = useState(false);
  // A paid plan of this listing's size that is not on any listing yet (bought on the pricing page, or
  // its tagging failed): offered instead of charging again.
  const [unclaimed, setUnclaimed] = useState<HostPlanPurchaseSummary | null>(null);
  // A purchase whose payment is still confirming. While set, Continue never opens a second payment.
  const [confirmingPurchaseId, setConfirmingPurchaseId] = useState<number | null>(null);
  const mounted = useRef(true);
  const settling = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const pid = resolvePropertyId();
    if (!pid) return undefined;
    let cancelled = false;
    HostPlanPurchaseService.listMine()
      .then((purchases) => {
        if (cancelled) return;
        const mine = purchases || [];
        setProPaid(
          mine.some(
            (p) => p.status === 'SUCCESS' && p.propertyId != null && String(p.propertyId) === String(pid),
          ),
        );
        const fits = (p: HostPlanPurchaseSummary): boolean =>
          p.propertyId == null && (!bhkLocked || p.bhkType === listingBhk);
        setUnclaimed(mine.find((p) => p.status === 'SUCCESS' && fits(p)) || null);
        const pending = mine.find(
          (p) =>
            p.status === 'PENDING' &&
            fits(p) &&
            Date.now() - new Date(p.createdAt).getTime() < PENDING_PURCHASE_MAX_AGE_MS,
        );
        if (pending) void settlePurchase(pending.id);
      })
      .catch(() => {
        // unknown: treated as not paid, so Continue still asks for the payment
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Starter needs no payment: note the choice on the listing and go straight to Review. */
  const continueWithStarter = (): void => {
    setPlan('starter');
    const pid = resolvePropertyId();
    if (pid) {
      // Best effort: if this fails the host still proceeds, and an admin can Starter-tag the listing.
      try {
        sessionStorage.setItem(`starterChosen_${pid}`, '1');
      } catch {
        // storage unavailable: the review page falls back to asking the server
      }
      HostPlanPurchaseService.chooseStarter(pid).catch(() => {});
    }
    goToReview();
  };

  /** Host Pro, not yet on this listing: finish a confirming payment, use an unclaimed plan, or pay. */
  const startProFlow = (): void => {
    if (confirmingPurchaseId !== null) {
      // never start a second payment while one is confirming; just check on it again
      if (!settling.current) void settlePurchase(confirmingPurchaseId);
      return;
    }
    if (unclaimed) {
      void activatePurchase(unclaimed.id);
      return;
    }
    if (!paying) void onSubscribe();
  };

  /** Continue: Starter goes to Review; Host Pro opens the Zoho payment widget until it is paid for. */
  const onContinue = (): void => {
    if (plan === 'starter') {
      continueWithStarter();
      return;
    }
    if (!proPaid) {
      startProFlow();
      return;
    }
    goToReview();
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!bhkTouched.current) setBhk(listingBhk);
  }, [listingBhk]);

  const chosenBhk = bhkLocked ? listingBhk : bhk;
  const price: number = PRICE_TABLE[chosenBhk][duration];
  const durationMeta = DURATIONS.find((d: any) => d.key === duration);

  const goToReview = (): void => {
    const id = resolvePropertyId();
    navigate(
      id
        ? `/host/acknowledgement?id=${id}${isImportFlow ? '&flow=import' : ''}`
        : '/host/acknowledgement',
    );
  };

  const doBack = (): void => {
    const id = resolvePropertyId();
    if (isImportFlow && id) {
      navigate(`/host/import-listing?id=${id}&step=missing`);
      return;
    }
    navigate(id ? `/host/house-rules?id=${id}` : '/host/house-rules');
  };
  const { backHandler, BackConfirmModal } = useOnboardingBackConfirm(doBack);

  /* Same Host Pro checkout as the public pricing page (Zoho Payments). */
  const payWithZoho = async (paymentsSession: ZohoPaymentsSession, purchaseId: number): Promise<void> => {
    let paid = false;
    try {
      paid = await openZohoPayment(paymentsSession, String(price));
    } finally {
      setPaying(false);
    }
    // A payment id is not a settled payment (UPI and netbanking can still fail or stay pending), so
    // wait for the backend to confirm it before treating the plan as bought.
    if (paid) await settlePurchase(purchaseId);
  };

  /** The plan is bought: mark the account as premium and put the plan on this listing. */
  const activatePurchase = async (purchaseId: number): Promise<void> => {
    markAccountPremium();
    const pid = resolvePropertyId();
    // Try to put the plan on this listing right away. The plan only fits a listing of the same
    // BHK size, so if that fails the host picks a listing on the existing tagging screen.
    try {
      if (!pid) throw new Error('No property id');
      await HostPlanPurchaseService.tagProperty(purchaseId, Number(pid));
      notification.success({
        message: 'Host Pro activated',
        description: 'This listing now carries your Host Pro plan.',
      });
      goToReview();
    } catch (_) {
      window.location.href = `/host/pro/select-listing?purchaseId=${purchaseId}`;
    }
  };

  /** Polls the payment until it leaves PENDING; a purchase still pending after that keeps Continue blocked. */
  const settlePurchase = async (purchaseId: number): Promise<void> => {
    if (settling.current) return;
    settling.current = true;
    setConfirmingPurchaseId(purchaseId);
    try {
      const status = await settlePurchaseStatus(purchaseId, () => mounted.current, VERIFY_POLL_ATTEMPTS, VERIFY_POLL_INTERVAL_MS);
      if (status === 'ABORTED') return;
      if (status === 'SUCCESS') {
        setConfirmingPurchaseId(null);
        settling.current = false;
        await activatePurchase(purchaseId);
        return;
      }
      if (status === 'FAILED') {
        setConfirmingPurchaseId(null);
        notification.error({
          message: 'Payment failed',
          description: 'Your payment did not go through. Please try again.',
        });
        return;
      }
      notification.info({
        message: 'Still confirming your payment',
        description:
          "Your bank hasn't confirmed this payment yet. Please don't pay again - tap Continue in a minute to check again.",
      });
    } finally {
      settling.current = false;
    }
  };

  const onSubscribe = async (): Promise<void> => {
    // Nothing is created (no pending purchase) when the payment popup could not open anyway.
    if (!ensureZohoReady()) return;
    setPaying(true);
    try {
      const billingCycle = durationMeta?.billingCycle || duration;
      const session = await HostPlanPurchaseService.createSession(chosenBhk, billingCycle);
      await payWithZoho(session.zoho.payments_session, session.purchaseId);
    } catch (err: any) {
      notification.error({
        message: 'Could not start payment',
        description: err?.response?.data?.error || 'Please try again in a moment.',
      });
      setPaying(false);
    }
  };

  return (
    <OnboardingShell
      section={isImportFlow ? 2 : 8}
      railItems={isImportFlow ? IMPORT_SECTIONS : undefined}
      maxWidth="46rem"
      hideActions
      headerBack={backHandler}
      modal={BackConfirmModal}
    >
      <Banner>
        <span className="tick">
          <FiCheck size={20} />
        </span>
        <span>You’ve done the hard part - just a few more steps to go.</span>
      </Banner>

      <Heading>Choose your hosting plan</Heading>

      <Plans>
        <Card
          $selected={plan === 'starter'}
          role="button"
          tabIndex={0}
          aria-pressed={plan === 'starter'}
          onClick={() => setPlan('starter')}
          onKeyDown={(e) => {
            if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              setPlan('starter');
            }
          }}
        >
          <h2>Starter</h2>
          <p className="desc">Try UNPAQD for free - up to 2 bookings, no pre-payment.</p>
          <div className="grow" />
          <div className="price">₹0</div>
          <OutlineButton
            type="button"
            $block
            onClick={(e) => {
              e.stopPropagation();
              continueWithStarter();
            }}
            style={{ height: 44, fontSize: 14, color: ob.primary }}
          >
            <FiCheck size={16} /> Get Started <FiArrowRight size={16} />
          </OutlineButton>
        </Card>

        <Card
          $selected={plan === 'pro'}
          onClick={() => setPlan('pro')}
        >
          <span className="ribbon" aria-hidden>
            <span>RECOMMENDED</span>
          </span>
          <h2>Host Pro</h2>
          <p className="desc">0% commission, unrestricted chats, and priority support.</p>

          <div className="label">Property configuration</div>
          <div className="select">
            <select
              aria-label="Property configuration"
              value={chosenBhk}
              disabled={bhkLocked}
              title={bhkLocked ? "Matches your listing's size" : undefined}
              onChange={(e) => {
                bhkTouched.current = true;
                setBhk(Number(e.target.value));
              }}
            >
              {(BHK_OPTIONS as number[]).map((option) => (
                <option key={option} value={option}>
                  {option} BHK
                </option>
              ))}
            </select>
            <FiChevronDown size={18} />
          </div>

          <div className="durations" role="group" aria-label="Plan duration">
            {(DURATIONS as { key: DurationKey; label: string }[]).map((item) => (
              <button
                key={item.key}
                type="button"
                aria-pressed={duration === item.key}
                onClick={() => setDuration(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="price-line">
            <strong>{formatInr(price)}</strong>
            <span> for {durationMeta?.months} months</span>
          </div>

          <PrimaryButton
            type="button"
            $block
            disabled={paying}
            onClick={startProFlow}
            style={{ height: 44, fontSize: 14 }}
          >
            {paying
              ? 'Starting payment…'
              : confirmingPurchaseId !== null
              ? 'Confirming your payment…'
              : unclaimed
              ? 'Use the plan you already bought'
              : 'Click for Payment'}{' '}
            <FiArrowRight size={16} />
          </PrimaryButton>
        </Card>
      </Plans>

      <Compare>
        <h2>Compare plans</h2>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">Features</th>
                <th scope="col">Starter</th>
                <th scope="col" className="pro">
                  Host Pro
                </th>
              </tr>
            </thead>
            <tbody>
              {(featureComparison as any[]).map((row) => {
                const tooltipOnPro = row.tooltipOn === 'pro';
                const cell = (value: string) =>
                  value === 'check' ? (
                    <FiCheck size={18} className="yes" aria-label="Included" />
                  ) : value === 'cross' ? (
                    <FiX size={16} className="no" aria-label="Not included" />
                  ) : (
                    value
                  );
                return (
                  <tr key={row.feature}>
                    <th scope="row">
                      {row.feature}
                      {row.tooltip && !tooltipOnPro ? (
                        <InfoTooltip content={row.tooltip} openOnHover dark />
                      ) : null}
                    </th>
                    <td>{cell(row.free)}</td>
                    <td className="pro">
                      <span className="inline">
                        {cell(row.pro)}
                        {row.tooltip && tooltipOnPro ? (
                          <InfoTooltip content={row.tooltip} openOnHover dark />
                        ) : null}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Compare>

      <Footer>
        <GhostButton type="button" onClick={backHandler}>
          Back
        </GhostButton>
        <PrimaryButton type="button" onClick={onContinue}>
          Continue <FiArrowRight size={16} />
        </PrimaryButton>
      </Footer>
    </OnboardingShell>
  );
};

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid rgba(255, 113, 15, 0.2);
  border-radius: 16px;
  background: rgba(255, 240, 230, 0.6);
  font-size: 14px;
  font-weight: 500;
  .tick {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border-radius: 50%;
    background: ${ob.primarySoft};
    color: ${ob.primary};
  }
`;

const Heading = styled.h1`
  margin: 0;
  font-family: ${ob.fontDisplay};
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${ob.text};
  ${bp.sm} {
    font-size: 28px;
  }
`;

const Plans = styled.div`
  display: grid;
  gap: 16px;
  align-items: stretch;
  ${bp.md} {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.section<{ $selected?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: ${({ $selected }) => ($selected ? '21px 19px 19px' : '22px 20px 20px')};
  border: ${({ $selected }) => ($selected ? `2px solid ${ob.primary}` : `1px solid ${ob.borderStrong}`)};
  border-radius: 24px;
  background: #fff;
  box-shadow: ${({ $selected }) => ($selected ? ob.shadowStrong : ob.shadowCard)};
  cursor: pointer;
  transition: border-color 140ms ease, box-shadow 140ms ease;
  &:hover {
    border-color: ${ob.primary};
  }
  &:focus-visible {
    outline: 2px solid ${ob.primary};
    outline-offset: 3px;
  }
  h2 {
    margin: 0;
    font-family: ${ob.fontDisplay};
    font-size: 19px;
    font-weight: 700;
    color: ${ob.text};
  }
  .desc {
    margin: 8px 0 0;
    max-width: 240px;
    font-size: 13.5px;
    line-height: 1.6;
    color: ${ob.muted};
  }
  .grow {
    flex: 1;
    min-height: 24px;
  }
  .price {
    margin-bottom: 16px;
    font-family: ${ob.fontDisplay};
    font-size: 34px;
    font-weight: 800;
    line-height: 1;
    color: ${ob.text};
  }
  .ribbon {
    position: absolute;
    top: 0;
    right: 0;
    width: 110px;
    height: 110px;
    overflow: hidden;
    pointer-events: none;
  }
  .ribbon span {
    position: absolute;
    top: 24px;
    right: -34px;
    width: 150px;
    padding: 4px 0;
    transform: rotate(45deg);
    background: ${ob.primary};
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-align: center;
  }
  .label {
    margin-top: 18px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${ob.muted};
  }
  .select {
    position: relative;
    margin-top: 8px;
  }
  .select select {
    width: 100%;
    height: 48px;
    padding: 0 40px 0 16px;
    border: 1px solid rgba(255, 113, 15, 0.45);
    border-radius: 999px;
    background: ${ob.surface};
    font-family: inherit;
    font-size: 15px;
    font-weight: 500;
    color: ${ob.text};
    appearance: none;
    cursor: pointer;
    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px ${ob.ring};
    }
  }
  .select svg {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: ${ob.muted};
    pointer-events: none;
  }
  .durations {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2px;
    margin-top: 12px;
    padding: 5px;
    border-radius: 999px;
    background: ${ob.mutedBg};
  }
  .durations button {
    height: 38px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: ${ob.muted};
    cursor: pointer;
    transition: all 140ms ease;
    &[aria-pressed='true'] {
      background: ${ob.primary};
      color: #fff;
      box-shadow: ${ob.shadowBrand};
    }
  }
  .price-line {
    margin: 18px 0 16px;
    font-size: 13.5px;
    color: ${ob.muted};
    strong {
      font-family: ${ob.fontDisplay};
      font-size: 20px;
      color: ${ob.text};
    }
  }
`;

const Compare = styled.section`
  margin-top: 8px;
  h2 {
    margin: 0 0 12px;
    font-family: ${ob.fontDisplay};
    font-size: 19px;
    font-weight: 700;
    color: ${ob.text};
  }
  .scroll {
    overflow-x: auto;
    border: 1px solid ${ob.borderStrong};
    border-radius: 20px;
    background: #fff;
  }
  table {
    width: 100%;
    min-width: 480px;
    border-collapse: collapse;
  }
  th,
  td {
    padding: 14px 16px;
    text-align: left;
    font-size: 14px;
    color: ${ob.text};
    border-top: 1px solid ${ob.border};
  }
  thead th {
    border-top: 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${ob.muted};
    background: ${ob.surface};
  }
  tbody th {
    font-weight: 500;
  }
  th:not(:first-child),
  td {
    text-align: center;
    width: 26%;
  }
  .pro {
    background: rgba(255, 240, 230, 0.45);
    font-weight: 600;
  }
  thead th.pro {
    color: ${ob.primary};
  }
  .inline {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .yes {
    color: ${ob.primary};
  }
  .no {
    color: ${ob.muted};
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0 40px;
`;

/** Admins never see the plan screen: their listings are plan-free, so forward them to Review. */
const HostingPlan: React.FC = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const admin = isAdminUser();
  useEffect(() => {
    if (!admin) return;
    const params = new URLSearchParams(search);
    const id = params.get('id') || localStorage.getItem('newPropertId') || localStorage.getItem('id');
    const flow = params.get('flow') === 'import' ? '&flow=import' : '';
    navigate(id ? `/host/acknowledgement?id=${id}${flow}` : '/host/acknowledgement', { replace: true });
  }, [admin, search, navigate]);
  return admin ? null : <HostingPlanScreen />;
};

export default HostingPlan;
